import { motion } from 'framer-motion';
import { Shield, Users, Globe, Award, CheckCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';

const HERO_BG = '/assets/images/about.png';
const ABOUT_IMG = '/assets/images/about2.png';
const GOVERNANCE_BG = '/assets/images/about.png';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="about-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#2D3E55]/85 to-[#1a2634]/85"></div>

        <div className="container-custom relative z-10">
          <motion.div
            className="text-center text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              About His Presence Chapel
            </h1>
            <p className="font-body text-lg text-white/80">
              A faith-based nonprofit organization headquartered in Texas, United States,
              committed to compassionate service and community empowerment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="section-padding" data-testid="about-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-[#F7B500]/10 rounded-full text-[#2D3E55] font-body text-sm font-medium mb-6">
                Who We Are
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-6">
                Faith in Action Since Day One
              </h2>
              <div className="space-y-4 font-body text-slate-600 leading-relaxed">
                <p>
                  His Presence Chapel Inc. is founded upon the belief that faith should be expressed
                  not only through worship and teaching but also through compassionate service that
                  uplifts individuals and strengthens communities.
                </p>
                <p>
                  The organization believes that genuine faith is demonstrated through actions that
                  restore dignity, promote hope, and provide meaningful support to those facing hardship.
                </p>
                <p>
                  This belief shapes the mission of His Presence Chapel Inc. and inspires the humanitarian,
                  empowerment, and community initiatives carried out through its programs.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={ABOUT_IMG}
                alt="Community service"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#F15A24] text-white p-6 rounded-xl shadow-lg">
                <p className="font-heading text-3xl font-bold">501(c)(3)</p>
                <p className="font-body text-sm text-white/90">Registered Nonprofit</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Governance Structure */}
      <section
        className="relative section-padding bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${GOVERNANCE_BG})` }}
        data-testid="governance-section"
      >
        <div className="absolute inset-0 bg-white/85"></div>

        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-[#2D3E55]/10 rounded-full text-[#2D3E55] font-body text-sm font-medium mb-4">
              Governance Structure
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
              Transparency & Accountability
            </h2>
            <p className="font-body text-lg text-slate-600">
              His Presence Chapel Inc. and its initiatives are committed to operating with transparency,
              responsible stewardship, and accountability.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card
                className="text-center h-full hover:shadow-lg transition-shadow bg-white/95 backdrop-blur-sm"
                data-testid="governance-rahvai"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#2D3E55]/10 flex items-center justify-center mb-6">
                    <Globe className="text-[#2D3E55]" size={32} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-3">RAHVAI</h3>
                  <p className="font-body text-slate-600 text-sm">
                    Humanitarian outreach supporting vulnerable communities across Africa
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card
                className="text-center h-full hover:shadow-lg transition-shadow bg-white/95 backdrop-blur-sm"
                data-testid="governance-seeal"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#F15A24]/10 flex items-center justify-center mb-6">
                    <Users className="text-[#F15A24]" size={32} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-3">SEEAL Africa</h3>
                  <p className="font-body text-slate-600 text-sm">
                    Economic empowerment through skills training and enterprise development
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card
                className="text-center h-full hover:shadow-lg transition-shadow bg-white/95 backdrop-blur-sm"
                data-testid="governance-bhna"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#6EC1E4]/10 flex items-center justify-center mb-6">
                    <Shield className="text-[#6EC1E4]" size={32} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-3">BHNA</h3>
                  <p className="font-body text-slate-600 text-sm">
                    Behavioral Health Navigation & Workforce Agency support
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" data-testid="values-section">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: 'Faith', desc: 'Grounded in spiritual calling' },
              { icon: Users, title: 'Leadership', desc: 'Guiding with integrity' },
              { icon: Shield, title: 'Compassion', desc: 'Serving with heart' },
              { icon: CheckCircle, title: 'Credibility', desc: 'Acting with accountability' },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-[#F7B500]/10 flex items-center justify-center mb-4">
                  <value.icon className="text-[#F7B500]" size={28} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#2D3E55] mb-2">{value.title}</h3>
                <p className="font-body text-sm text-slate-600">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}